import apiClient from "@/api/client";

/** Converts a base64url VAPID key to the BufferSource pushManager expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export function isWebPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function fetchVapidPublicKey(): Promise<string | null> {
  const r = await apiClient.get<{ data: { key: string | null } }>(
    "/realtime/webpush/public-key/",
  );
  return r.data.data.key;
}

export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isWebPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** True in Brave (desktop + Android), which ships with Google push messaging disabled. */
export async function isBraveBrowser(): Promise<boolean> {
  const nav = navigator as Navigator & {
    brave?: { isBrave?: () => Promise<boolean> };
  };
  try {
    return !!(await nav.brave?.isBrave?.());
  } catch {
    return false;
  }
}

export const BRAVE_PUSH_HINT =
  "Brave domyślnie blokuje usługę powiadomień push. Wejdź w brave://settings/privacy, " +
  "włącz „Używaj usług Google do przesyłania wiadomości push” (Use Google services for " +
  "push messaging), uruchom ponownie przeglądarkę i kliknij dzwonek jeszcze raz.";

function errorName(e: unknown): string {
  return e instanceof Error ? e.name : "";
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * Turns a `pushManager.subscribe()` rejection into a message the user can act
 * on. The notorious case is Brave: the notification permission prompt is shown
 * and granted, but the actual subscription then fails with
 * `AbortError: Registration failed - push service error` because Brave ships
 * with "Use Google services for push messaging" turned off.
 */
export async function describeSubscribeError(e: unknown): Promise<string> {
  const name = errorName(e);
  const msg = errorMessage(e);
  if (name === "NotAllowedError") {
    return (
      "Przeglądarka odmówiła subskrypcji push — sprawdź uprawnienia witryny " +
      "(ikona kłódki w pasku adresu)."
    );
  }
  if (await isBraveBrowser()) {
    return BRAVE_PUSH_HINT;
  }
  if (/push service/i.test(msg) || name === "AbortError") {
    return (
      "Przeglądarka nie mogła połączyć się z usługą powiadomień push " +
      "(sprawdź, czy nie jest blokowana przez ustawienia prywatności lub sieć)."
    );
  }
  return `Przeglądarka nie mogła zarejestrować subskrypcji push (${msg || name || "nieznany błąd"}).`;
}

/** Requests permission, subscribes in the browser and registers on the backend. */
export async function subscribeWebPush(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission === "denied") {
    throw new Error(
      "Powiadomienia są zablokowane w przeglądarce — odblokuj je klikając ikonę kłódki w pasku adresu.",
    );
  }
  if (permission !== "granted") {
    throw new Error("Brak zgody na powiadomienia w przeglądarce.");
  }
  const key = await fetchVapidPublicKey();
  if (!key) {
    throw new Error("Powiadomienia push nie są skonfigurowane na serwerze.");
  }
  let reg: ServiceWorkerRegistration;
  try {
    reg = await getServiceWorkerRegistration();
  } catch (e) {
    throw new Error(
      `Nie udało się zarejestrować obsługi powiadomień w przeglądarce (${errorMessage(e)}).`,
    );
  }
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      });
    } catch (e) {
      throw new Error(await describeSubscribeError(e));
    }
  }
  await apiClient.post("/realtime/webpush/subscriptions/", subscription.toJSON());
}

/** Dismisses any OS notification for the given room (called on room open/read). */
export async function dismissRoomNotification(roomId: string): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return;
  const notifications = await reg.getNotifications({ tag: `gg-room-${roomId}` });
  notifications.forEach((n) => n.close());
}

/** Unsubscribes in the browser and removes the subscription on the backend. */
export async function unsubscribeWebPush(): Promise<void> {
  const subscription = await getExistingSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await apiClient.delete("/realtime/webpush/subscriptions/", {
    data: { endpoint },
  });
}
